import { Formik } from 'formik';
import React, { useRef } from 'react';
import styled from 'styled-components';
import { Button, Buttons, CancelButton, Drawer, Field, Form, FormMessage } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useForm } from '~/hooks';
import { emptyStringToNull, mergeValues } from '~/utils';
import * as Yup from 'yup';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: 1.625rem;
  margin-bottom: 1.625rem;
`;

const SetupDescription = styled.p`
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

export default function XeroExportSetupDrawer({ integration, onClose, onSave }) {
  const formRef = useRef();
  const toast = useToast();
  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ status, message, isSubmitting }, form] = useForm();

  const initialValues = mergeValues(
    {
      brandingTheme: '',
      nonTaxableCode: '',
    },
    integration.configuration,
  );

  return (
    <Drawer
      title="Xero Invoice Export Setup"
      isOpen
      onBeforeClose={({ setIsOpen }) => setIsOpen(false)}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleSubmit = async (values) => {
          try {
            form.submit();

            const settings = emptyStringToNull(values);

            if (integration.isConnected) {
              await api.www.workspaces(workspace.id).xeroExport.updateSettings({ settings });
            } else {
              await api.www.workspaces(workspace.id).xeroExport.setup({ settings });
            }

            onSave();
            form.done();
            closeDrawer();
          } catch ({ message }) {
            toast.error(message);
          }
        };
        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              brandingTheme: Yup.string().max(255),
              nonTaxableCode: Yup.string().max(255),
            })}>
            {(formik) => {
              return (
                <Form>
                  {status && <FormMessage.Error>{message}</FormMessage.Error>}
                  <Content>
                    <SetupDescription>
                      With this integration installed, the Invoice Detail report in Ruddr can be exported in CSV format
                      to be uploaded to Xero.
                    </SetupDescription>
                    <Form.Section title="Settings">
                      <Form.Control help="Enter the exact name of your custom Xero branding theme (optional).">
                        <Field.Text name="brandingTheme" placeholder="Branding Theme" maxLength={255} />
                      </Form.Control>
                      <Form.Control help="Enter the display name of the Xero tax rate for non-taxable items (e.g., 'Exempt Sales').">
                        <Field.Text
                          name="nonTaxableCode"
                          placeholder="Tax Code for Non Taxable items"
                          maxLength={255}
                        />
                      </Form.Control>
                    </Form.Section>
                  </Content>
                  <Drawer.Actions>
                    <Buttons align="right">
                      <CancelButton onClick={closeDrawer}>Close</CancelButton>
                      <Button isLoading={isSubmitting} type="submit" onClick={formik.submit}>
                        {integration.isConnected ? 'Save & Close' : 'Install'}
                      </Button>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}
